import {
  CALL_OUTCOMES,
  CALL_OUTCOME_LABELS,
  CONTACT_ROLES,
  CONTACT_ROLE_LABELS,
  EQUIPMENT_CONDITIONS,
  EQUIPMENT_TYPE_LABELS,
  EQUIPMENT_TYPES,
  type ConsentBasis,
  type ContactDTO,
  type ContactRole,
  type ContactVerificationStatus,
  type LogActivityResponse,
} from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button, Checkbox, Field, Input, Select, SidePanel, Textarea, useToast } from '../../../components/ui';
import { useLookups } from '../../../hooks/useMeta';
import { api, errorMessage, fieldErrors } from '../../../lib/api';
import { formatDateTime } from '../../../lib/format';
import { followUpBody } from '../../tasks/entryModel';
import { FollowUpFields, useFollowUp } from '../../tasks/FollowUpFields';
import { invalidateTaskQueries } from '../../tasks/taskQueries';
import { contactDisplayName } from '../LeadProfilePage';

const CALL_FOLLOW_UP_TITLE = 'Call back';
const REPLY_FOLLOW_UP_TITLE = 'Follow up on their reply';

/** Relationship to set with the log; empty = let the automatic rules move it (Cold → Contacted / Responded …). */
function RelationshipAfterField({ label, value, onChange }: { label: string; value: string; onChange: (key: string) => void }) {
  const { meta } = useLookups();
  return (
    <Field label={label} hint="Leave on automatic to let Hearth move it (e.g. Cold → Contacted after an attempt, → Responded after a conversation).">
      <Select value={value} onChange={(event) => onChange(event.target.value)} placeholder="Update automatically">
        {(meta?.relationships ?? [])
          .filter((r) => r.isActive)
          .map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
      </Select>
    </Field>
  );
}

export function LogCallPanel({ businessId, onClose, onSaved }: { businessId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<string>('connected');
  const [direction, setDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [summary, setSummary] = useState('');
  const [relationshipKey, setRelationshipKey] = useState('');
  const [body, setBody] = useState('');
  const [followUp, setFollowUp] = useFollowUp({ type: 'call', days: 1 });
  const followUpInvalid = followUp.enabled && followUpBody(followUp, CALL_FOLLOW_UP_TITLE, new Date()) === null;
  const save = useMutation({
    mutationFn: async () => {
      const next = followUpBody(followUp, CALL_FOLLOW_UP_TITLE, new Date());
      await api.post<LogActivityResponse>(`/api/leads/${businessId}/activities`, {
        kind: 'call',
        outcome,
        direction,
        summary: summary.trim() || null,
        body: body.trim() || null,
        ...(next ? { followUp: next } : {}),
        ...(relationshipKey ? { relationshipKey } : {}),
      });
      return next;
    },
    onSuccess: async (next) => {
      toast.success(next ? `Call logged · follow-up on ${formatDateTime(next.dueAt)}` : 'Call logged');
      await invalidateTaskQueries(queryClient, businessId);
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title="Log a call"
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={followUpInvalid} onClick={() => save.mutate()}>
            Log call
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Direction">
          <Select value={direction} onChange={(event) => setDirection(event.target.value as 'outbound' | 'inbound')}>
            <option value="outbound">We called them</option>
            <option value="inbound">They called us</option>
          </Select>
        </Field>
        <Field label="Outcome">
          <Select value={outcome} onChange={(event) => setOutcome(event.target.value)}>
            {CALL_OUTCOMES.map((value) => (
              <option key={value} value={value}>
                {CALL_OUTCOME_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Summary" hint="One line for the timeline.">
          <Input value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="Spoke to the owner about replacing the deck oven" />
        </Field>
        <Field label="What was said?">
          <Textarea rows={4} value={body} onChange={(event) => setBody(event.target.value)} />
        </Field>
        <RelationshipAfterField label="Relationship after this call" value={relationshipKey} onChange={setRelationshipKey} />
        <FollowUpFields value={followUp} onChange={setFollowUp} defaultTitle={CALL_FOLLOW_UP_TITLE} />
      </div>
    </SidePanel>
  );
}

export function LogReplyPanel({
  businessId,
  contacts,
  onClose,
  onSaved,
}: {
  businessId: string;
  contacts: ContactDTO[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [contactId, setContactId] = useState(contacts.find((c) => c.isPrimary)?.id ?? '');
  const [summary, setSummary] = useState('');
  const [relationshipKey, setRelationshipKey] = useState('');
  const [body, setBody] = useState('');
  const [followUp, setFollowUp] = useFollowUp({ type: 'follow_up', days: 1 });
  const followUpInvalid = followUp.enabled && followUpBody(followUp, REPLY_FOLLOW_UP_TITLE, new Date()) === null;
  const save = useMutation({
    mutationFn: async () => {
      const next = followUpBody(followUp, REPLY_FOLLOW_UP_TITLE, new Date());
      await api.post<LogActivityResponse>(`/api/leads/${businessId}/activities`, {
        kind: 'email_received',
        contactId: contactId || null,
        summary: summary.trim() || 'Reply received',
        body: body.trim() || null,
        ...(next ? { followUp: next } : {}),
        ...(relationshipKey ? { relationshipKey } : {}),
      });
      return next;
    },
    onSuccess: async (next) => {
      toast.success(`Reply logged${next ? ` · follow-up on ${formatDateTime(next.dueAt)}` : ''} — automated follow-ups for this lead are paused`);
      await invalidateTaskQueries(queryClient, businessId);
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title="Log a reply"
      description="Use this when they answered by phone, in person or in a mailbox the CRM does not read."
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={followUpInvalid} onClick={() => save.mutate()}>
            Log reply
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="From">
          <Select value={contactId} onChange={(event) => setContactId(event.target.value)} placeholder="Unknown contact">
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contactDisplayName(contact)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Summary">
          <Input value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="Wants pricing for a double-deck oven" />
        </Field>
        <Field label="What did they say?">
          <Textarea rows={4} value={body} onChange={(event) => setBody(event.target.value)} />
        </Field>
        <RelationshipAfterField label="Relationship after this reply" value={relationshipKey} onChange={setRelationshipKey} />
        <FollowUpFields value={followUp} onChange={setFollowUp} defaultTitle={REPLY_FOLLOW_UP_TITLE} />
      </div>
    </SidePanel>
  );
}

export function NotePanel({ businessId, onClose, onSaved }: { businessId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [body, setBody] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/api/leads/${businessId}/notes`, { body: body.trim() }),
    onSuccess: () => {
      toast.success('Note added');
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title="Add a note"
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={body.trim() === ''} onClick={() => save.mutate()}>
            Save note
          </Button>
        </div>
      }
    >
      <Textarea rows={8} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Owner mentioned the oven overheats on Friday nights…" autoFocus />
    </SidePanel>
  );
}

export function ContactPanel({
  businessId,
  contact,
  onClose,
  onSaved,
}: {
  businessId: string;
  contact: ContactDTO | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<{
    firstName: string;
    lastName: string;
    title: string;
    role: ContactRole;
    email: string;
    phone: string;
    isPrimary: boolean;
    isDecisionMaker: boolean;
    verificationStatus: ContactVerificationStatus;
    consentBasis: ConsentBasis;
    notes: string;
  }>({
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    title: contact?.title ?? '',
    role: contact?.role ?? 'owner',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    isPrimary: contact?.isPrimary ?? false,
    isDecisionMaker: contact?.isDecisionMaker ?? true,
    verificationStatus: contact?.verificationStatus ?? 'unverified',
    consentBasis: contact?.consentBasis ?? 'none',
    notes: contact?.notes ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        title: form.title || null,
        role: form.role,
        email: form.email || null,
        phone: form.phone || null,
        isPrimary: form.isPrimary,
        isDecisionMaker: form.isDecisionMaker,
        verificationStatus: form.verificationStatus,
        consentBasis: form.consentBasis,
        consentObtainedAt: form.consentBasis === 'none' ? null : new Date().toISOString(),
        notes: form.notes || null,
      };
      return contact
        ? api.patch<{ businessId: string }>(`/api/contacts/${contact.id}`, { ...payload, version: contact.version })
        : api.post(`/api/leads/${businessId}/contacts`, payload);
    },
    onSuccess: () => {
      toast.success(contact ? 'Contact updated' : 'Contact added');
      onSaved();
      onClose();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      toast.error(errorMessage(error));
    },
  });

  const remove = async () => {
    if (!contact) return;
    if (!(await window.confirm('Remove this contact?'))) return;
    try {
      await api.delete(`/api/contacts/${contact.id}`);
      toast.success('Contact removed');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title={contact ? 'Edit contact' : 'Add a contact'}
      width="md"
      footer={
        <div className="flex items-center justify-between">
          {contact ? (
            <Button variant="danger" icon={Trash2} onClick={() => void remove()}>
              Remove
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              {contact ? 'Save' : 'Add contact'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="First name" error={errors.firstName}>
          <Input value={form.firstName} onChange={(event) => set('firstName', event.target.value)} />
        </Field>
        <Field label="Last name" error={errors.lastName}>
          <Input value={form.lastName} onChange={(event) => set('lastName', event.target.value)} />
        </Field>
        <Field label="Role">
          <Select value={form.role} onChange={(event) => set('role', event.target.value as ContactRole)}>
            {CONTACT_ROLES.map((role) => (
              <option key={role} value={role}>
                {CONTACT_ROLE_LABELS[role]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title">
          <Input value={form.title} onChange={(event) => set('title', event.target.value)} />
        </Field>
        <Field label="Email" error={errors.email}>
          <Input value={form.email} onChange={(event) => set('email', event.target.value)} />
        </Field>
        <Field label="Phone">
          <Input value={form.phone} onChange={(event) => set('phone', event.target.value)} />
        </Field>
        <Field label="Verification">
          <Select value={form.verificationStatus} onChange={(event) => set('verificationStatus', event.target.value as ContactVerificationStatus)}>
            <option value="unverified">Unverified</option>
            <option value="verified">Verified</option>
            <option value="outdated">Outdated</option>
            <option value="invalid">Invalid</option>
          </Select>
        </Field>
        <Field label="Consent to email">
          <Select value={form.consentBasis} onChange={(event) => set('consentBasis', event.target.value as ConsentBasis)}>
            <option value="none">No consent recorded</option>
            <option value="implied_published">Implied — address published</option>
            <option value="implied_business_relationship">Implied — existing relationship</option>
            <option value="implied_inquiry">Implied — they asked us something</option>
            <option value="express">Express consent</option>
            <option value="withdrawn">Withdrawn</option>
          </Select>
        </Field>
        <div className="sm:col-span-2 space-y-2">
          <Checkbox label="Primary contact" checked={form.isPrimary} onChange={(event) => set('isPrimary', event.target.checked)} />
          <Checkbox label="Decision maker" checked={form.isDecisionMaker} onChange={(event) => set('isDecisionMaker', event.target.checked)} />
        </div>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea rows={3} value={form.notes} onChange={(event) => set('notes', event.target.value)} />
        </Field>
      </div>
    </SidePanel>
  );
}

export function EquipmentPanel({ businessId, onClose, onSaved }: { businessId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [equipmentType, setEquipmentType] = useState<string>('deck_oven');
  const [brand, setBrand] = useState('');
  const [installYear, setInstallYear] = useState('');
  const [condition, setCondition] = useState<string>('unknown');
  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/leads/${businessId}/equipment`, {
        equipmentType,
        brand: brand || null,
        installYear: installYear ? Number(installYear) : null,
        condition,
      }),
    onSuccess: () => {
      toast.success('Equipment recorded');
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title="Add kitchen equipment"
      description="Old ovens are the strongest buying signal — record the year if you know it."
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Add equipment
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Equipment">
          <Select value={equipmentType} onChange={(event) => setEquipmentType(event.target.value)}>
            {EQUIPMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {EQUIPMENT_TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Brand">
          <Input value={brand} onChange={(event) => setBrand(event.target.value)} placeholder="Brickline" />
        </Field>
        <Field label="Install year" hint="Used for the “oven looks old” scoring rule.">
          <Input type="number" min={1900} max={2100} value={installYear} onChange={(event) => setInstallYear(event.target.value)} placeholder="2008" />
        </Field>
        <Field label="Condition">
          <Select value={condition} onChange={(event) => setCondition(event.target.value)}>
            {EQUIPMENT_CONDITIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </SidePanel>
  );
}

export function NewOpportunityPanel({ businessId, onClose, onCreated }: { businessId: string; onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const { meta } = useLookups();
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [probability, setProbability] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>(`/api/leads/${businessId}/opportunities`, {
        title: title.trim(),
        products: amount
          ? [{ description: title.trim(), quantity: 1, unitPrice: Number(amount), productId: null }]
          : [],
        probability: probability ? Number(probability) : null,
        expectedCloseDate: expectedCloseDate || null,
      }),
    onSuccess: () => {
      toast.success('Opportunity created');
      onCreated();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title="New opportunity"
      description="Track the deal separately from the relationship so repeat customers keep their history."
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={title.trim() === ''} onClick={() => save.mutate()}>
            Create opportunity
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="What are they buying?" required>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Replace the deck oven" />
        </Field>
        <Field label="Estimated value" hint="Leave empty to add line items later.">
          <Input type="number" min={0} value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="18500" />
        </Field>
        <Field label="Equipment category" hint="Optional — used when quoting from the catalog.">
          <Select value={category} onChange={(event) => setCategory(event.target.value)} placeholder="Not specified">
            {(meta?.productCategories ?? []).map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Expected close">
          <Input type="date" value={expectedCloseDate} onChange={(event) => setExpectedCloseDate(event.target.value)} />
        </Field>
        <Field label="Probability %" hint="Leave empty to use the stage default.">
          <Input type="number" min={0} max={100} value={probability} onChange={(event) => setProbability(event.target.value)} />
        </Field>
      </div>
    </SidePanel>
  );
}
